import React, {useEffect, useRef, useState} from "react";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import axios from "axios";
import {Button, Form, InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {useNavigate} from "react-router-dom";
import {baseUrl} from "../Univ";
import "./AIChat.css";
const AIChat = ({agentCode = "DRGHIS"}) => {
    // ======================================================
    // STATES
    // ======================================================
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [sessionId, setSessionId] = useState("");
    const [messages, setMessages] = useState([{
        sender: "ai", text: "👋 Welcome to DrHiS AI Assistant.\n\nHow can I help you today?"
    }]);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [inputLanguage, setInputLanguage] = useState("en-IN");
    const [voiceLanguage, setVoiceLanguage] = useState("en-IN");
    const [voiceInputEnabled, setVoiceInputEnabled] = useState(false);
    // ======================================================
    // VOICE
    // ======================================================
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [lastAIReply, setLastAIReply] = useState("");
    const silenceTimerRef = useRef(null);
    const {
        transcript, listening, resetTranscript
    } = useSpeechRecognition();
    const navigate = useNavigate();
    // ======================================================
// AUTO SCROLL
// ======================================================
    const chatEndRef = useRef(null);
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({
            behavior: "smooth"
        });
    }, [messages]);
    useEffect(() => {
        if (!voiceInputEnabled) return;
        if (transcript) {
            setMessage(transcript);
            // reset silence timer
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
            }
            silenceTimerRef.current = setTimeout(() => {
                sendMessage(transcript);
                resetTranscript();
                setMessage("");
            }, 2000);
        }
        return () => {
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
            }
        };
    }, [transcript]);
// ======================================================
// START VOICE
// ======================================================
    const startVoice = () => {
        setVoiceInputEnabled(true);
        resetTranscript();
        SpeechRecognition.startListening({
            continuous: true, interimResults: true, language: inputLanguage
        });
    };
// ======================================================
// TOGGLE SPEAKER
// ======================================================
    const toggleVoice = () => {
        if (voiceEnabled) {
            window.speechSynthesis.cancel();
            setVoiceEnabled(false);
        } else {
            setVoiceEnabled(true);
            if (lastAIReply) {
                const speech = new SpeechSynthesisUtterance(formatForSpeech(cleanForSpeech(lastAIReply)));
                speech.lang = voiceLanguage;
                speech.rate = 1;
                speech.pitch = 1;
                speech.volume = 1;
                window.speechSynthesis.speak(speech);
            }
        }
    };
// ======================================================
// CLEAN TEXT FOR SPEECH
// ======================================================
    const cleanForSpeech = (text) => {
        return text
            .replace(/```[\s\S]*?```/g, "")
            .replace(/[*#>`]/g, "")
            .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
            .replace(/[📦📂📋🚚⚖📊⚠•]/g, "")
            .replace(/[=]{2,}/g, "")
            .replace(/[-]{2,}/g, "")
            .replace(/\n+/g, ". ")
            .replace(/\s+/g, " ")
            .trim();
    };
// ======================================================
// FORMAT NUMBERS FOR SPEECH
// ======================================================
    const formatForSpeech = (text) => {
        // Mobile Numbers (10 digits)
        text = text.replace(/\b\d{10}\b/g, (num) => {
            return num.split("").join(" ");
        });
        // OTP (4-8 digits)
        text = text.replace(/\b\d{4,8}\b/g, (num) => {
            return num.split("").join(" ");
        });
        // Invoice numbers like INV12345
        text = text.replace(/\b([A-Za-z]+)(\d+)\b/g, (_, letters, digits) => {
            return letters + " " + digits.split("").join(" ");
        });
        return text;
    };
// ======================================================
// SPEAK RESPONSE
// ======================================================
    const speak = (text) => {
        if (!voiceEnabled || !text) {
            return;
        }
        try {
            const cleanText = formatForSpeech(cleanForSpeech(text));
            if (!cleanText) {
                return;
            }
            // ==================================================
            // CHECK WEB SPEECH SUPPORT
            // ==================================================
            if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance !== "function") {
                console.warn("DrHiS: Speech synthesis unavailable");
                return;
            }
            // ==================================================
            // STOP PREVIOUS SPEECH
            // ==================================================
            window.speechSynthesis.cancel();
            // ==================================================
            // VOICES
            // ==================================================
            const voices = window.speechSynthesis.getVoices ? window.speechSynthesis.getVoices() : [];
            const selectedVoice = voices.find(v => v.lang === voiceLanguage);
            // ==================================================
            // UTTERANCE
            // ==================================================
            const speech = new SpeechSynthesisUtterance(cleanText);
            speech.lang = voiceLanguage;
            speech.rate = 1;
            speech.pitch = 1;
            speech.volume = 1;
            if (selectedVoice) {
                speech.voice = selectedVoice;
            }
            // ==================================================
            // EVENTS
            // ==================================================
            speech.onstart = () => {
                console.log("DrHiS TTS START", voiceLanguage);
            };
            speech.onend = () => {
                console.log("DrHiS TTS END");
            };
            speech.onerror = (event) => {
                console.log("DrHiS TTS ERROR", event);
            };
            // ==================================================
            // SPEAK
            // ==================================================
            window.speechSynthesis.resume();
            window.speechSynthesis.speak(speech);
        } catch (error) {
            console.error("DrHiS TTS ERROR:", error);
            // IMPORTANT:
            // Do not allow TTS failure to make
            // the API request look like it failed.
        }
    };
    useEffect(() => {
        window.speechSynthesis.onvoiceschanged = () => {
            window.speechSynthesis.getVoices();
        };
    }, []);
// ======================================================
// COPY MESSAGE
// ======================================================
    const copyMessage = (text) => {
        navigator.clipboard.writeText(text);
    };
// ======================================================
// SEND MESSAGE
// ======================================================
    const sendMessage = async (voiceMessage = null) => {
        setVoiceInputEnabled(false);
        SpeechRecognition.stopListening();
        if (loading) return;
        const text = typeof voiceMessage === "string" ? voiceMessage : message;
        if (!text.trim() && !selectedFile) return;
        setMessages(prev => [...prev, {
            sender: "user", text: text || `📎 ${selectedFile.name}`
        }]);
        if (!voiceMessage) setMessage("");
        setLoading(true);
        try {
            let response;
            // ==========================================
            // FILE UPLOAD
            // ==========================================
            if (selectedFile) {
                const formData = new FormData();
                formData.append("question", text);
                formData.append("session_id", sessionId);
                formData.append("agent_code", agentCode);
                formData.append("file", selectedFile);
                response = await axios.post(baseUrl + "ai/chat/", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });
            }

                // ==========================================
                // NORMAL CHAT
            // ==========================================
            else {
                response = await axios.post(baseUrl + "ai/chat/", {
                    question: text, session_id: sessionId, agent_code: agentCode
                });
            }
            if (response.data.session_id) setSessionId(response.data.session_id);
            let aiReply = "No response.";
            if (typeof response.data.response === "string") {
                aiReply = response.data.response;
            } else if (response.data.response && typeof response.data.response === "object") {
                aiReply = response.data.response.response || JSON.stringify(response.data.response);
            }
            setMessages(prev => [...prev, {
                sender: "ai", text: aiReply
            }]);
            setLastAIReply(aiReply);
            try {
                speak(aiReply);
            } catch (ttsError) {
                console.warn("DrHiS TTS failed:", ttsError);
            }
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
            console.error(error);
            setMessages(prev => [...prev, {
                sender: "ai", text: "❌ Unable to process request."
            }]);
        } finally {
            setLoading(false);
        }
    };
// ======================================================
// CLEAR CHAT
// ======================================================
    const clearChat = () => {
        window.speechSynthesis.cancel();
        setSessionId("");
        setMessages([{
            sender: "ai", text: "👋 Welcome to DrHiS ERP AI Assistant.\n\nHow can I help you today?"
        }]);
        setMessage("");
        resetTranscript();
    };
// =====================================
// SELECT FILE
// =====================================
    const chooseFile = () => {
        fileInputRef.current.click();
    };
// =====================================
// FILE SELECTED
// =====================================
    const fileSelected = (e) => {
        if (!e.target.files.length) return;
        setSelectedFile(e.target.files[0]);
    };
// =====================================
// REMOVE FILE
// =====================================
    const removeFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
// ======================================================
// FEEDBACK
// ======================================================
    const likeMessage = async (index) => {
        console.log("Liked", index);
        // Future API
        // await axios.post(baseUrl+"ai/feedback/",{
        //     session_id:sessionId,
        //     message:messages[index].text,
        //     feedback:"like"
        // });
    };
    const dislikeMessage = async (index) => {
        console.log("Disliked", index);
        // Future API
        // await axios.post(baseUrl+"ai/feedback/",{
        //     session_id:sessionId,
        //     message:messages[index].text,
        //     feedback:"dislike"
        // });
    };
    // ======================================================
    // COMPONENT END
    // ======================================================
    return (<>

        {!open && (<button

            className="ai-float-btn"

            onClick={() => setOpen(true)}

        >

            🤖

        </button>)}

        {open && (<div className="ai-chat-window">

            <div className="ai-chat-header">

                <div className="ai-header-left">

                    <div className="ai-header-icon">
                        🤖
                    </div>

                    <div className="ai-header-text">

                        <div className="ai-header-title">
                            DrHiS Assistant
                        </div>

                        <div className="ai-header-subtitle">

                            {loading ? (<>🟢 Thinking...</>) : (<>🟢 AI Online</>)}

                        </div>

                    </div>

                </div>

                <div className="ai-header-actions">

                    <button
                        className="ai-header-btn"
                        title="Voice Reply"
                        onClick={toggleVoice}
                    >
                        {voiceEnabled ? "🔊" : "🔇"}
                    </button>

                    <button
                        className="ai-header-btn"
                        title="Voice Input"
                        onClick={() => {
                            if (voiceInputEnabled) {
                                setVoiceInputEnabled(false);
                                SpeechRecognition.stopListening();
                            } else {
                                startVoice();
                            }
                        }}
                    >
                        {voiceInputEnabled ? "🛑" : "🎤"}
                    </button>
                    <select

                        value={voiceLanguage}

                        onChange={(e) => {
                            console.log("Selected voice:", e.target.value);
                            setVoiceLanguage(e.target.value);
                        }}

                    >

                        <option value="en-IN">
                            English
                        </option>

                        <option value="ta-IN">
                            தமிழ்
                        </option>

                        <option value="hi-IN">
                            हिन्दி
                        </option>

                    </select>
                    <button
                        className="ai-header-btn"
                        title="Clear Chat"
                        onClick={clearChat}
                    >
                        🗑️
                    </button>

                    <button
                        className="ai-header-btn"
                        title="Close"
                        onClick={() => setOpen(false)}
                    >
                        ✕
                    </button>

                </div>

            </div>

            <div className="ai-chat-body">

                {messages.map((msg, index) => (<div

                    key={index}

                    className={msg.sender === "user" ? "message-row user-row" : "message-row ai-row"}

                >

                    {msg.sender === "ai" && <div className="avatar ai-avatar">

                        🤖

                    </div>}

                    <div

                        className={msg.sender === "user" ? "user-bubble" : "ai-bubble"}

                    >

                        <div className="message-text">
                            {msg.text.split("\n").map((line, i) => {
                                const match = line.match(
                                    /^(UHID\s*:\s*)(\d+)(.*)$/i
                                );
                                if (match) {
                                    return (
                                        <div key={i}>
                                            {match[1]}
                                            <a
                                                href={`/bill-details/${match[2]}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(
                                                        `/emr-details/${match[2]}`
                                                    );
                                                }}
                                                className="ai-uhid-link"
                                            >
                                                {match[2]}
                                            </a>
                                            {match[3]}
                                        </div>
                                    );
                                }
                                return (
                                    <div key={i}>
                                        {line}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="message-footer">

                            <small>

                                {new Date().toLocaleTimeString([], {
                                    hour: "2-digit", minute: "2-digit"
                                })}

                            </small>

                            {msg.sender === "ai" && <div>

                                <OverlayTrigger

                                    placement="top"

                                    overlay={<Tooltip>

                                        Copy

                                    </Tooltip>}

                                >

                                    <Button

                                        variant="link"

                                        size="sm"

                                        onClick={() => copyMessage(msg.text)}

                                    >

                                        📋

                                    </Button>

                                </OverlayTrigger>


                            </div>}

                        </div>

                    </div>

                    {msg.sender === "user" && <div className="avatar user-avatar">

                        👤

                    </div>}

                </div>))}

                {loading && <div className="message-row ai-row">

                    <div className="avatar ai-avatar">

                        🤖

                    </div>

                    <div className="typing-bubble">

                        <span></span>

                        <span></span>

                        <span></span>

                    </div>

                </div>}

                <div ref={chatEndRef}></div>

            </div>

            <div>
                <input

                    type="file"

                    ref={fileInputRef}

                    hidden

                    accept="

        image/*,

        .pdf,

        .doc,

        .docx,

        .xls,

        .xlsx,

        .csv,

        .txt

    "

                    onChange={fileSelected}

                />
                {selectedFile && (<div className="selected-file">

                    📎

                    <strong>

                        {selectedFile.name}

                    </strong>

                    <Button

                        size="sm"

                        variant="danger"

                        className="ms-2"

                        onClick={removeFile}

                    >

                        ✕

                    </Button>

                </div>)}
                <InputGroup>
                    <Button

                        variant="secondary"

                        onClick={chooseFile}

                    >

                        📎

                    </Button>
                    <Form.Control

                        placeholder="Ask anything about DrHiS ERP..."

                        value={message}

                        disabled={loading}

                        onChange={(e) => setMessage(e.target.value)}

                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}

                    />

                    <Button
                        variant="success"
                        disabled={loading}
                        onClick={() => sendMessage()}
                    >
                        {loading ? "..." : "Send"}
                    </Button>

                </InputGroup>

            </div>

        </div>)}

    </>);
};
export default AIChat;